/**
 * 342 (#933): the host sales statement as rows, the reference sheet's layout: one row per figure, one column per car
 * and a Total. The on-screen table and the CSV both come from `statementRows`, so they cannot disagree.
 */
export interface StatementCar {
  car_id: string;
  label: string;
  plate_number: string | null;
  revenue_minor: number;
  /** 370: CARii's platform fee on this car's bookings (absent from an older server). */
  platform_fee_minor?: number;
  penalty_minor: number;
  compensation_minor: number;
  self_booking_minor: number;
  remarks: string[];
  rental_days: number;
  monthly_cost_minor: number | null;
  daily_cost_minor: number | null;
  supplier_total_cost_minor: number;
  commission_rate_bps: number | null;
  commission_minor: number;
  /** 409: CARii's share of the car's penalties at the penalty rate, part of `commission_minor` (absent: older server). */
  penalty_commission_minor?: number;
  net_minor: number;
  settings_missing: string[];
  bookings: Array<{
    id: string;
    status: string;
    pickup_at: string;
    return_at: string;
    amount_minor: number;
  }>;
  adjustments: Array<{
    id: string;
    car_id: string;
    kind: string;
    on: string;
    amount_minor: number;
    note: string;
    /** 371: a penalty from the penalty tracker (edited there). */
    penalty_id?: string;
  }>;
}
export interface Statement {
  supplier: { id: string; name: string };
  /** 369 (#1237): present when the statement is one host's (`id: null` is "No host"). */
  from: string;
  to: string;
  months: string[];
  cars: StatementCar[];
  /** 370: whether this statement period has been settled (null: nothing recorded). */
  payment?: { paid: boolean; note: string; by_name: string; at: string | null } | null;
  totals: {
    revenue_minor: number;
    platform_fee_minor?: number;
    penalty_minor: number;
    compensation_minor: number;
    self_booking_minor: number;
    rental_days: number;
    supplier_total_cost_minor: number;
    commission_minor: number;
    penalty_commission_minor?: number;
    net_minor: number;
  };
}

/** Ringgit with two decimals and no symbol, as on the sheet. */
export const ringgit = (minor: number) => (minor / 100).toFixed(2);

/** 370: where a row sits on the screen (the sheet's groups) and whether its cells are amounts. */
export type RowSection = 'head' | 'income' | 'costs' | 'fee' | 'net';
export interface StatementRow {
  label: string;
  cells: string[];
  section: RowSection;
  money: boolean;
}

export function statementRows(s: Statement): StatementRow[] {
  const money = (pick: (c: StatementCar) => number, total: number) => [
    ...s.cars.map((c) => ringgit(pick(c))),
    ringgit(total),
  ];
  const row = (label: string, cells: string[], section: RowSection, isMoney: boolean) => ({
    label,
    cells,
    section,
    money: isMoney,
  });
  return [
    row('Cars', [...s.cars.map((c) => c.plate_number ?? c.label), 'Total'], 'head', false),
    row(
      'Revenue (RM)',
      money((c) => c.revenue_minor, s.totals.revenue_minor),
      'income',
      true,
    ),
    row(
      'Penalty (RM)',
      money((c) => c.penalty_minor, s.totals.penalty_minor),
      'income',
      true,
    ),
    row(
      'Compensation (RM)',
      money((c) => c.compensation_minor, s.totals.compensation_minor),
      'income',
      true,
    ),
    row(
      'Self Booking (RM)',
      money((c) => c.self_booking_minor, s.totals.self_booking_minor),
      'income',
      true,
    ),
    row('Remark', [...s.cars.map((c) => c.remarks.join('; ')), ''], 'income', false),
    row(
      'Days Of Rental',
      [...s.cars.map((c) => String(c.rental_days)), String(s.totals.rental_days)],
      'costs',
      false,
    ),
    row(
      'Supplier Daily Cost (RM)',
      [...s.cars.map((c) => (c.daily_cost_minor === null ? '' : ringgit(c.daily_cost_minor))), ''],
      'costs',
      false,
    ),
    row(
      'Supplier Total Cost (RM)',
      money((c) => c.supplier_total_cost_minor, s.totals.supplier_total_cost_minor),
      'costs',
      true,
    ),
    row(
      'CARii Commission Rate',
      [
        ...s.cars.map((c) =>
          c.commission_rate_bps === null ? '' : String(c.commission_rate_bps / 10000),
        ),
        '',
      ],
      'costs',
      false,
    ),
    // 409 (#1471): the penalties are charged at the penalty rate, not the car's; this is that part of the commission.
    row(
      'of which Penalty Commission (RM)',
      money((c) => c.penalty_commission_minor ?? 0, s.totals.penalty_commission_minor ?? 0),
      'costs',
      true,
    ),
    row(
      'CARii Commission Amount (RM)',
      money((c) => c.commission_minor, s.totals.commission_minor),
      'costs',
      true,
    ),
    // 370: CARii's, shown beside the money but in neither net pay nor the commission base.
    row(
      'Platform Fee (RM)',
      money((c) => c.platform_fee_minor ?? 0, s.totals.platform_fee_minor ?? 0),
      'fee',
      true,
    ),
    row(
      'Net Pay (RM)',
      money((c) => c.net_minor, s.totals.net_minor),
      'net',
      true,
    ),
  ];
}

const quote = (v: string) => (/[",\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);

export function statementToCsv(s: Statement): string {
  const lines = [
    ['Supplier', s.supplier.name],
    ['From', s.from],
    ['To', s.to],
    ...statementRows(s).map((r) => [r.label, ...r.cells]),
    // 370: the sheet's "Payment Status" under Net Pay, with its note.
    ['Payment Status', s.payment?.paid ? 'TRUE' : 'FALSE', s.payment?.note ?? ''],
  ];
  return `${lines.map((l) => l.map(quote).join(',')).join('\r\n')}\r\n`;
}
